import { KeyPerformance } from '@/pages/components/key-performance/key-performance.tsx'
import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { DashboardComponent } from '@/pages/summary/components/dashboard/dashboard-component.tsx'
import { isV2Ready } from '@/utils/helpers.ts'
import { DashboardUserControls } from '@/pages/summary/components/dashboard/dashboard-user-controls.tsx'
import { useAppSelector } from '@/redux/hooks.ts'
import { selectThemeStyle } from '@/features/setting/setting.slice.ts'
import { SummaryInfo } from '@/pages/idea-list/components/idea-summary-info/idea-list-summary-info.tsx'
import { PositionListTable } from '@/pages/summary/components/position-list-table.tsx'
import { IdeaHistoryModal } from '@/pages/idea-list/components/idea-modal/idea-history/idea-history-modal.tsx'
import {
  SectionTitleBar,
  SectionTitleBarRef,
} from '@/pages/components/section-title-bar.tsx'
import { selectDashboardInput } from '@/redux/slices/dashboard-input.slice.ts'
import { GridRef } from '@/common/ag-grid/grid.types.ts'
import { useDispatch } from 'react-redux'
import { statisticsApi } from '@/apis/statistics/statistics-api.api.ts'
import { useTranslation } from 'react-i18next'

interface DashboardProps {
  bookId?: string
  /** Selected contributor id. Passed down to SummaryInfo and the position table. */
  contributor?: string
  /** When true, the position table ticker code is rendered as plain text without the navigation link. */
  disableTickerCodeLink?: boolean
}

const MemoizedPositionListTable = memo(PositionListTable)

export const Dashboard: React.FC<DashboardProps> = ({
  bookId,
  contributor,
  disableTickerCodeLink = false,
}) => {
  const { t } = useTranslation()
  const [showKeyPerformanceSection, setShowKeyPerformanceSection] =
    useState<boolean>(true)
  const [showIdeaHistoryModal, setShowIdeaHistoryModal] =
    useState<boolean>(false)
  const [historyIdeaId, setHistoryIdeaId] = useState<string | null>(null)
  const themeStyle = useAppSelector(selectThemeStyle)
  const dashboardInput = useAppSelector(selectDashboardInput)
  const sectionTitleRef = useRef<SectionTitleBarRef>(null)
  const infiniteIdeaGridRef = useRef<GridRef>(null)
  const dispatch = useDispatch()

  // Stabilize dateRange reference to avoid unnecessary SummaryInfo re-renders
  const summaryDateRange = useMemo<[string | null, string | null]>(
    () => [
      dashboardInput.dateRange[0] ?? null,
      dashboardInput.dateRange[1] ?? null,
    ],
    [dashboardInput.dateRange]
  )

  const handleReloadList = useCallback(() => {
    infiniteIdeaGridRef.current?.refresh()
  }, [])

  // Called after table data refreshes — invalidate statistics cache and update the title bar timestamp
  const handleTableRefreshed = useCallback(() => {
    dispatch(statisticsApi.util.invalidateTags(['statistics']))
    sectionTitleRef.current?.refreshTimestamp()
  }, [dispatch])

  const handleViewHistoryIdea = useCallback((id: string) => {
    setHistoryIdeaId(id)
    setShowIdeaHistoryModal(true)
  }, [])

  return (
    <>
      <div style={{ marginBottom: themeStyle.marginBase }}>
        <SummaryInfo
          contributor={contributor || ''}
          bookId={bookId || ''}
          dateRange={summaryDateRange}
        />
      </div>
      <DashboardUserControls />
      <SectionTitleBar
        ref={sectionTitleRef}
        title={t('dashboard.position_table')}
        reload={handleReloadList}
      />
      <MemoizedPositionListTable
        ref={infiniteIdeaGridRef}
        height={400}
        contributor={contributor}
        disableTickerCodeLink={disableTickerCodeLink}
        onRefreshed={handleTableRefreshed}
        onViewHistoryIdea={handleViewHistoryIdea}
      />
      <IdeaHistoryModal
        showHistoryModal={showIdeaHistoryModal}
        ideaId={historyIdeaId}
        handleCloseModal={() => {
          setHistoryIdeaId(null)
          setShowIdeaHistoryModal(false)
        }}
      />
      {isV2Ready && (
        <div style={{ marginBottom: themeStyle.marginSM }}>
          <KeyPerformance
            taskCompletionRate={0.65}
            numOfPendingTasks={3}
            currentRanking={23}
            totalRankingAmount={100}
            currentPnl={12200234}
            topPnl={345678890}
            showKeyPerformanceSection={showKeyPerformanceSection}
            setShowKeyPerformanceSection={() =>
              setShowKeyPerformanceSection(!showKeyPerformanceSection)
            }
            canEdit={true}
            bookId={bookId}
          />
        </div>
      )}
      <DashboardComponent bookId={bookId} />
    </>
  )
}
